import { useLazyQuery, useQuery } from '@apollo/client';
import { useDealForecastStore } from '@openx/components-deal/lib/utils/context/DealForecastContext/DealForecastProvider.tsx';
import type { PackageType, UsePackageFetch } from '@openx/components-deal/lib/utils/context/DealHookTypes.ts';
import type { UsePackageLazyFetch } from '@openx/types/package/PackageHookTypes.ts';
import type { Package } from '@openx/types/package.ts';
import { DealForecastActionType } from '@openx/types/store/deal.ts';
import { Intersect } from '@openx/types/targeting/targetingValuesTypes.ts';
import { useEffect } from 'react';
import { GetOpenxCuratedPackageDocument } from '../../../graphql/query/openxCuratedPackages/openxCuratedPackage.generated.tsx';
import { GetPackageDocument } from '../../../graphql/query/packages/GetPackage.generated.tsx';

function applyAudienceToPackage(pckg: Package, audienceId: string) {
  pckg.targeting.audience = {
    openaudience_custom: {
      op: Intersect.INTERSECTS,
      val: `openaudience-${audienceId}`,
    },
  };
}

export const usePackageFetch: UsePackageFetch = (deal): PackageType => {
  const { package_uid } = deal;
  const packageObj = (deal as { package_obj?: Package }).package_obj;

  const { dispatch } = useDealForecastStore();

  // Curated packages are identified by numeric IDs; regular packages use UUIDs
  const isCuratedPackage = !!package_uid && /^\d+$/.test(package_uid);

  const {
    data,
    loading: packagesLoading,
    refetch,
  } = useQuery(GetPackageDocument, {
    skip: !package_uid || isCuratedPackage || !!packageObj,
    variables: { id: package_uid as string },
  });

  const { data: openxCuratedData, loading: openxCuratedLoading } = useQuery(GetOpenxCuratedPackageDocument, {
    skip: !package_uid || !isCuratedPackage || !!packageObj,
    variables: {
      id: package_uid!,
    },
  });

  const loading = isCuratedPackage ? openxCuratedLoading : packagesLoading;

  useEffect(() => {
    dispatch({ package_loading: loading, type: DealForecastActionType.DEAL_FORECAST_PACKAGE_LOADING });
  }, [loading, dispatch]);

  if (packageObj) {
    return { loading: false, pckg: packageObj, refetch };
  }

  const packageById = data?.packageById;
  let pckg: Package | null = null;

  if (packageById) {
    pckg = structuredClone(packageById.obj);

    if (pckg && packageById.audience_id) {
      applyAudienceToPackage(pckg, packageById.audience_id);
    }
  } else if (openxCuratedData?.openxCuratedPackage?.obj) {
    pckg = structuredClone(openxCuratedData.openxCuratedPackage.obj);
  }

  return {
    loading,
    pckg: pckg ?? undefined,
    refetch,
  };
};

export const usePackageLazyFetch: UsePackageLazyFetch = (packageId?: string | null, onCompleted?) => {
  const [fn, { loading }] = useLazyQuery(GetPackageDocument, {
    onCompleted: data => {
      const packageById = data?.packageById;
      const pckg = packageById ? structuredClone(packageById.obj) : null;

      // Audience can be attached to package in non exported state which would not be reflected to targeting until audience
      // is "exported" Targeting update depending on package.audience_id field is required to show audience (independent of
      // status) on UI
      if (pckg && packageById?.audience_id) {
        applyAudienceToPackage(pckg, packageById.audience_id);
      }
      onCompleted?.(pckg || null);
    },
    variables: { id: packageId as string },
  });

  return {
    fetchPackage: fn,
    loading,
  };
};
